import React, { useState, useRef, useEffect } from 'react';
import { EyeIcon } from '../icons/eye.icon';
import { EyeOffIcon } from '../icons/eyeOff.icon';
import { ChevronDownIcon, CrossIcon } from '@umanforge/common';
import ValidationIndicator from '../others/validationIndicator.component';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@common_components/others/infoTooltip.component';

interface InputTextProps {
  className?: string;
  label?: string;
  placeholder?: string;
  tooltip?: string;
  value?: string | number;
  type?: 'text' | 'number' | 'password' | 'textarea';
  min?: number | null;
  max?: number | null;
  textSize?: string;
  maxCharacter?: number;
  onChange?: ( value: string ) => void;
  disable?: boolean;
  isRequired?: boolean;
  labelPosition?: 'top' | 'left';
  icon?: React.ReactNode;
}

const processNumberInput = ( value: string, min?: number | null, max?: number | null ): string => {
  const hasMin = ( min !== undefined && min !== null );
  const effectiveMin = hasMin ? min : null;
  const result = value.replace( /[^\d.\-]/g, '' );
  if ( result === '' || result === '-' || result === '.' || result === '-.' || result === '.-' ) {
    return result;
  }
  const numValue = Number( result );
  if ( isNaN( numValue ) ) {
    return '';
  }
  if ( effectiveMin !== null && numValue < ( effectiveMin as number ) ) {
    return String( effectiveMin );
  }
  if ( max !== undefined && max !== null && numValue > max ) {
    return String( max );
  }
  return result;
};

const applyCharacterLimit = ( value: string, maxCharacter?: number ): string => {
  if ( !maxCharacter ) {
    return value;
  }
  return value.length > maxCharacter ? value.slice( 0, maxCharacter ) : value;
};

const getInputPlaceholder = ( placeholder?: string, type?: string, t?: ( key: string ) => string ): string => {
  if ( placeholder ) {
    return placeholder;
  }
  if ( type === 'password' ) {
    return t!( 'common:enter_a_password' );
  }
  if ( type === 'number' ) {
    return t!( 'common:enter_a_number' );
  }
  return t!( 'common:enter_a_text' );
};

const getContainerClasses = ( className: string, labelPosition: string ): string => {
  return `h-fit rounded-md ${className} ${labelPosition === 'left' ? 'flex flex-col md:flex-row md:items-center' : ''}`;
};

const getLabelContainerClasses = ( textSize: string, labelPosition: string ): string => {
  return labelPosition === 'top'
    ? 'flex gap-2 relative items-center w-full'
    : `flex gap-2 relative items-center w-full md:${textSize}`;
};

const getInputWrapperClasses = ( labelPosition: string ): string => {
  return `relative w-full ${labelPosition === 'top' ? 'mt-2' : 'mt-2 md:mt-0'} flex items-center`;
};

const getInputClasses = ( icon?: React.ReactNode, disable?: boolean, type?: string ): string => {
  const paddingLeft = ( icon && type !== 'number' ) ? 'pl-16' : 'pl-4';
  const paddingRight = type === 'number' ? 'pr-4' : 'pr-4';
  const baseClasses = 'p-2 w-full shadow min-h-11 outline-none rounded-md border-[1px]';
  const conditionalClasses = disable
    ? 'bg-gray-base-veryLight border-gray-base-veryLight text-gray-500 cursor-not-allowed'
    : 'bg-white hover:border-black';
  const focusClasses = 'focus:border-black focus:ring-1 focus:ring-black';

  return `${paddingLeft} ${paddingRight} ${baseClasses} ${conditionalClasses} ${focusClasses}`;
};

const InputText: React.FC<InputTextProps> = ( {
  className = 'w-full',
  label,
  placeholder,
  isRequired = false,
  value = '',
  type = 'text',
  tooltip,
  textSize = 'w-3/12 mr-4',
  disable = false,
  min,
  max,
  icon,
  maxCharacter,
  onChange,
  labelPosition = 'left',
} ) => {
  const [ inputValue, setInputValue ] = useState<string>( String( value ) );
  const [ showPassword, setShowPassword ] = useState( false );
  const [ isFocus, setIsFocus ] = useState( false );
  const [ isResetHover, setIsResetHover ] = useState( false );
  const [ isIncrementHover, setIsIncrementHover ] = useState( false );
  const [ isDecrementHover, setIsDecrementHover ] = useState( false );
  const inputRef = useRef<HTMLInputElement>( null );
  const textareaRef = useRef<HTMLTextAreaElement>( null );
  const { t } = useTranslation();

  useEffect( () => {
    setInputValue( String( value ) );
  }, [ value ] );

  const handleChange = ( newValue: string ) => {
    let result = newValue;

    if ( type === 'number' ) {
      result = processNumberInput( result, min, max );
      if ( result === '' || result === '-' || result === '.' || result === '-.' || result === '.-' ) {
        setInputValue( result );
        if ( onChange !== undefined ) {
          onChange( result );
        }
        return;
      }
    }

    result = applyCharacterLimit( result, maxCharacter );

    if ( inputValue !== result ) {
      setInputValue( result );
    }
    if ( onChange !== undefined ) {
      onChange( result );
    }
  };

  const increment = () => {
    if ( disable || type !== 'number' ) {
      return;
    }
    const hasMin = ( min !== undefined && min !== null );
    const current = Number( value !== undefined && value !== null && String( value ) !== '' ? value : ( hasMin ? min : 0 ) );
    const next = max !== undefined && max !== null ? Math.min( current + 1, max ) : current + 1;
    handleChange( String( next ) );
  };

  const decrement = () => {
    if ( disable || type !== 'number' ) {
      return;
    }
    const hasMin = ( min !== undefined && min !== null );
    const current = Number( value !== undefined && value !== null && String( value ) !== '' ? value : ( hasMin ? min : 0 ) );
    const next = hasMin ? Math.max( current - 1, min as number ) : ( current - 1 );
    handleChange( String( next ) );
  };

  const handleKeyDown = ( e: React.KeyboardEvent<HTMLElement> ) => {
    if ( type === 'number' ) {
      const hasMin = ( min !== undefined && min !== null );
      const effectiveMin = hasMin ? min : null;
      const allowMinus = effectiveMin === null || ( effectiveMin !== null && ( effectiveMin as number ) < 0 );
      const allowedKeys = [
        'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End', '.',
      ];
      if ( allowMinus ) { allowedKeys.push( '-' ); }
      if ( !/^\d$/.test( e.key ) && !allowedKeys.includes( e.key ) ) {
        e.preventDefault();
      }
      if ( e.key === 'ArrowUp' ) {
        e.preventDefault();
        increment();
      } else if ( e.key === 'ArrowDown' ) {
        e.preventDefault();
        decrement();
      }
    }
  };

  const handlePaste = ( e: React.ClipboardEvent<HTMLElement> ) => {
    if ( type === 'number' ) {
      const pasted = e.clipboardData.getData( 'text' );
      const hasMin = ( min !== undefined && min !== null );
      const effectiveMin = hasMin ? min : null;
      const allowMinus = effectiveMin === null || ( effectiveMin !== null && ( effectiveMin as number ) < 0 );
      const pasteRegex = allowMinus ? /^[-]?\d*\.?\d*$/ : /^\d*\.?\d*$/;
      if ( !pasteRegex.test( pasted ) ) {
        e.preventDefault();
      }
    }
  };

  const getInputType = (): string => {
    return type === 'password' && showPassword ? 'text' : type;
  };

  const togglePasswordVisibility = () => {
    setShowPassword( prev => {
      return !prev;
    } );
  };

  const CharacterCounter = () => {
    if ( !isFocus || maxCharacter === undefined ) {
      return null;
    }

    const counterClasses = [
      'absolute h-full ml-1 -top-3.5 z-10 transition ease-in-out delay-75',
      isRequired ? 'right-3' : 'right-0.5',
      isRequired && inputValue.length !== 0 ? 'translate-x-2.5' : ''
    ].join( ' ' );

    const spanClasses = [
      'h-fit w-fit px-1 py-0.5 rounded-md text-white text-[10.5px] font-bold',
      maxCharacter !== undefined && inputValue.length >= maxCharacter ? 'bg-red-400' : 'bg-gray-400'
    ].join( ' ' );

    return (
      <div className={counterClasses}>
        <span className={spanClasses}>
          {inputValue.length} {maxCharacter !== undefined && ( '/ ' + maxCharacter )}
        </span>
      </div>
    );
  };

  const PasswordToggle = () => {
    if ( type !== 'password' ) {
      return null;
    }

    const iconClasses = `cursor-pointer hover:bg-gray-300 trans-fast rounded-full p-1 ${disable ? 'opacity-30 pointer-events-none' : ''}`;

    return (
      <div className="absolute right-0 top-0 pr-2 h-full flex items-center gap-2">
        {showPassword ? (
          <EyeIcon size={7} onClick={togglePasswordVisibility} className={iconClasses} />
        ) : (
          <EyeOffIcon size={7} onClick={togglePasswordVisibility} className={iconClasses} />
        )}
      </div>
    );
  };

  return (
    <div className={getContainerClasses( className, labelPosition )}>
      {label && (
        <div className={getLabelContainerClasses( textSize, labelPosition )}>
          <label className='text-gray-600 truncate'>
            {label}
          </label>
          {tooltip && (
            <InfoTooltip label={label} tooltip={tooltip} />
          )}
        </div>
      )}

      <div className={getInputWrapperClasses( labelPosition )}>
        <ValidationIndicator isRequired={isRequired} isValid={( isRequired && inputValue !== '' )} />
        {icon && type !== 'number' && <div className={`absolute left-4 text-gray-500 ${ disable && 'cursor-not-allowed' }`}>{icon}</div>}

        {
          type === 'number' ?
            (
              <div
                className={`flex w-full py-1 min-h-11 justify-between items-center trans-fast shadow border rounded-md relative
                  ${isFocus && 'border-black ring-black ring-1'}
                  ${disable ? 'bg-gray-base-veryLight cursor-not-allowed border-gray-base-veryLight' : 'bg-white hover:border-black'}
                `}
              >
                <div className='flex w-full h-full items-center'>
                  {icon && <div className='text-gray-600 ml-4'>{icon}</div>}
                  <input
                    onFocus={() => { setIsFocus( !disable && true ); }}
                    onBlur={() => { setIsFocus( false ); }}
                    ref={inputRef}
                    disabled={disable}
                    value={inputValue}
                    type={getInputType()}
                    placeholder={getInputPlaceholder( placeholder, type, t )}
                    min={min !== null ? min : undefined}
                    max={max !== null ? max : undefined}
                    onChange={event => { handleChange( event.target.value ); }}
                    onKeyDown={handleKeyDown}
                    className='truncate w-full ml-4 bg-transparent border-none outline-none'
                    onPaste={handlePaste}
                  />

                  {/* Reset and Arrow buttons inside input */}
                  <div className="flex items-center gap-1 pr-2">
                    {/* Reset button */}
                    <button
                      type="button"
                      title={t( 'common:reset' )}
                      onClick={() => {
                        if ( disable ) { return; }
                        handleChange( '' );
                        setTimeout( () => { inputRef.current?.focus(); }, 0 );
                      }}
                      onMouseEnter={() => {return setIsResetHover( true );}}
                      onMouseLeave={() => {return setIsResetHover( false );}}
                      className={`h-8 w-8 flex items-center justify-center rounded trans-fast ${disable ? 'opacity-30 pointer-events-none' : ''}`}
                    >
                      <div style={{ color: isResetHover ? '#999999' : '#cccccc' }} className="font-bold">
                        <CrossIcon className="w-5 h-5" />
                      </div>
                    </button>

                    {/* Separator */}
                    <div className="h-6 w-px" style={{ backgroundColor: '#999999' }}></div>

                    {/* Arrow buttons up/down */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        onClick={increment}
                        disabled={disable}
                        onMouseEnter={() => {return setIsIncrementHover( true );}}
                        onMouseLeave={() => {return setIsIncrementHover( false );}}
                        className={`h-4 w-5 flex items-center justify-center rounded trans-fast ${disable ? 'opacity-30 pointer-events-none' : ''}`}
                      >
                        <ChevronDownIcon className={`w-3 h-3 rotate-180 ${isIncrementHover ? 'text-gray-600' : 'text-gray-600'}`} />
                      </button>
                      <button
                        type="button"
                        onClick={decrement}
                        disabled={disable}
                        onMouseEnter={() => {return setIsDecrementHover( true );}}
                        onMouseLeave={() => {return setIsDecrementHover( false );}}
                        className={`h-4 w-5 flex items-center justify-center rounded trans-fast ${disable ? 'opacity-30 pointer-events-none' : ''}`}
                      >
                        <ChevronDownIcon className={`w-3 h-3 ${isDecrementHover ? 'text-gray-600' : 'text-gray-600'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
            :
            type === 'textarea' ?
              (
                <>
                  <textarea
                    style={{ fieldSizing: 'content', maxHeight: '300px' }}
                    onFocus={() => { setIsFocus( !disable && true ); }}
                    onBlur={() => { setIsFocus( false ); }}
                    ref={textareaRef}
                    disabled={disable}
                    value={inputValue}
                    placeholder={getInputPlaceholder( placeholder, type, t )}
                    onChange={event => { handleChange( event.target.value ); }}
                    onKeyDown={handleKeyDown}
                    className={getInputClasses( icon, disable, type )}
                    onPaste={handlePaste}
                  />
                </>
              )
              :
              (
                <>
                  <input
                    onFocus={() => { setIsFocus( !disable && true ); }}
                    onBlur={() => { setIsFocus( false ); }}
                    ref={inputRef}
                    disabled={disable}
                    value={inputValue}
                    type={getInputType()}
                    placeholder={getInputPlaceholder( placeholder, type, t )}
                    onChange={event => { handleChange( event.target.value ); }}
                    onKeyDown={handleKeyDown}
                    className={getInputClasses( icon, disable, type )}
                    onPaste={handlePaste}
                  />

                  <PasswordToggle />
                </>
              )
        }

        <PasswordToggle />
        <CharacterCounter />
      </div>
    </div>
  );
};

export default InputText;
